import React, { useEffect, useState, useCallback } from "react";
import {
  Lightbulb, Brain, Sparkles, BookOpen, Rocket, Pencil,
  Trash2, Search, X, Plus, Tag, Clock, Filter, ChevronDown
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "idea", label: "Idea", icon: Lightbulb, color: "#E2A84B" },
  { id: "thought", label: "Thought", icon: Brain, color: "#8D6E63" },
  { id: "experience", label: "Experience", icon: BookOpen, color: "#5C8A5C" },
  { id: "inspiration", label: "Inspiration", icon: Sparkles, color: "#B5793F" },
  { id: "lesson-learned", label: "Lesson Learned", icon: Rocket, color: "#7B6BA4" },
  { id: "content-seed", label: "Content Seed", icon: Tag, color: "#5A8FA8" },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("idea");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterCat) params.set("category", filterCat);
    if (search) params.set("search", search);
    params.set("limit", "200");
    fetch(`${API}/api/admin/ideas?${params}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { items: [], total: 0 })
      .then((d) => { setIdeas(d.items); setTotal(d.total); })
      .catch(() => {});
    fetch(`${API}/api/admin/ideas/stats`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => {});
  }, [filterCat, search]);

  useEffect(() => { load(); }, [load]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const submit = async () => {
    if (!text.trim()) return toast.error("Write something first");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/ideas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: text.trim(), category, tags }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setText("");
      setTags([]);
      setTagInput("");
      toast.success("Captured!");
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
  };

  const updateIdea = async (id) => {
    if (!editText.trim()) return;
    try {
      const r = await fetch(`${API}/api/admin/ideas/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: editText.trim(), category: editCategory }),
      });
      if (!r.ok) throw new Error("Update failed");
      setEditingId(null);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteIdea = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await fetch(`${API}/api/admin/ideas/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const catInfo = (id) => CAT_MAP[id] || CAT_MAP.idea;

  return (
    <div className="space-y-6" data-testid="admin-ideas">

      {/* Stats bar */}
      {stats && (
        <div className="flex flex-wrap gap-3" data-testid="ideas-stats">
          <div className="rp-foam bg-white/55 px-5 py-3 flex items-center gap-2">
            <Lightbulb size={15} className="text-[#B5793F]" />
            <span className="text-2xl font-bold text-[#262016]">{stats.total}</span>
            <span className="text-xs text-[#8A7E6B]">total entries</span>
          </div>
          {CATEGORIES.filter((c) => stats.by_category?.[c.id]).map((c) => (
            <div key={c.id} className="rp-foam bg-white/55 px-4 py-3 flex items-center gap-2">
              <c.icon size={13} style={{ color: c.color }} />
              <span className="text-lg font-bold text-[#262016]">{stats.by_category[c.id]}</span>
              <span className="text-[11px] text-[#8A7E6B]">{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="rp-foam bg-white/70 p-5 md:p-6" data-testid="ideas-input-area">
        <div className="flex items-center gap-2 mb-3">
          <Brain size={16} className="text-[#B5793F]" />
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">Capture a Thought</p>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Dump your thought, idea, experience, or inspiration here..."
          rows={4}
          className="w-full resize-none rounded-lg border border-[#26201618] bg-white/80 px-4 py-3 text-sm text-[#262016] placeholder-[#8A7E6B] outline-none focus:border-[#B5793F] transition-colors"
          data-testid="ideas-text-input"
        />

        <div className="flex flex-wrap items-center gap-3 mt-3">
          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`ideas-cat-${c.id}`}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all flex items-center gap-1.5"
                style={{
                  background: category === c.id ? c.color + "20" : "transparent",
                  color: category === c.id ? c.color : "#8A7E6B",
                  border: `1px solid ${category === c.id ? c.color + "55" : "#26201615"}`,
                }}>
                <c.icon size={11} />
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Tag input */}
          <div className="flex items-center gap-1.5">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#B5793F15] text-[#B5793F] border border-[#B5793F30]">
                {t}
                <button onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-red-600"><X size={9} /></button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="+ tag"
              className="w-20 px-2 py-1 rounded-full text-[11px] border border-[#26201615] bg-transparent outline-none placeholder-[#8A7E6B] focus:border-[#B5793F]"
              data-testid="ideas-tag-input"
            />
          </div>

          <button
            onClick={submit}
            disabled={saving || !text.trim()}
            data-testid="ideas-submit-btn"
            className="px-5 py-2.5 rounded-full text-[12px] font-bold uppercase tracking-[0.08em] text-[#F6F1E5] transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
            {saving ? "Saving..." : "Capture"}
          </button>
        </div>

        <p className="text-[10px] text-[#8A7E6B] mt-2">Tip: Press Ctrl+Enter to save quickly. AI will reference these entries for content creation across your platform.</p>
      </div>

      {/* Search & Filter bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E6B]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries..."
            className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm border border-[#26201618] bg-white/60 outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]"
            data-testid="ideas-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7E6B] hover:text-[#262016]"><X size={13} /></button>
          )}
        </div>
        <button onClick={() => setShowFilters(!showFilters)} data-testid="ideas-filter-toggle"
                className="px-4 py-2.5 rounded-full text-xs font-semibold border border-[#26201618] text-[#5C5042] flex items-center gap-1.5 hover:bg-white/50">
          <Filter size={12} /> Filter <ChevronDown size={11} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-1.5" data-testid="ideas-filters">
          <button onClick={() => setFilterCat("")}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all"
                  style={{
                    background: !filterCat ? "#262016" : "transparent",
                    color: !filterCat ? "#EDE7D9" : "#8A7E6B",
                    border: `1px solid ${!filterCat ? "#262016" : "#26201618"}`,
                  }}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setFilterCat(c.id)}
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all flex items-center gap-1.5"
                    style={{
                      background: filterCat === c.id ? c.color + "20" : "transparent",
                      color: filterCat === c.id ? c.color : "#8A7E6B",
                      border: `1px solid ${filterCat === c.id ? c.color + "55" : "#26201618"}`,
                    }}>
              <c.icon size={10} /> {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Timeline list */}
      <div className="space-y-0" data-testid="ideas-list">
        {ideas.length === 0 && (
          <div className="rp-foam bg-white/55 p-8 text-center">
            <Lightbulb size={24} className="mx-auto mb-3 text-[#B5793F]" />
            <p className="text-sm text-[#8A7E6B]">{search || filterCat ? "No entries match your search." : "Start capturing your thoughts — they'll fuel your content."}</p>
          </div>
        )}
        {ideas.map((idea, i) => {
          const cat = catInfo(idea.category);
          const isEditing = editingId === idea.id;
          return (
            <div key={idea.id} className="flex gap-4" style={{ marginBottom: "2px" }} data-testid={`idea-${idea.id}`}>
              {/* Timeline connector */}
              <div className="flex flex-col items-center shrink-0 w-8">
                <div className="w-3 h-3 rounded-full border-2 shrink-0 mt-5" style={{ borderColor: cat.color, background: i === 0 ? cat.color : "transparent" }} />
                {i < ideas.length - 1 && <div className="flex-1 w-px bg-[#26201618]" />}
              </div>

              {/* Card */}
              <div className="flex-1 bg-white rounded-xl border border-[#d4cbb8] px-5 py-4 shadow-sm mb-3 min-w-0">
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3}
                              className="w-full resize-none rounded-lg border border-[#26201618] px-3 py-2 text-sm outline-none focus:border-[#B5793F]"
                              data-testid="idea-edit-text" />
                    <div className="flex gap-1.5 flex-wrap">
                      {CATEGORIES.map((c) => (
                        <button key={c.id} onClick={() => setEditCategory(c.id)}
                                className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
                                style={{
                                  background: editCategory === c.id ? c.color + "20" : "transparent",
                                  color: editCategory === c.id ? c.color : "#8A7E6B",
                                  border: `1px solid ${editCategory === c.id ? c.color + "55" : "#26201615"}`,
                                }}>{c.label}</button>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => updateIdea(idea.id)} data-testid="idea-save-edit"
                              className="px-4 py-1.5 rounded-full text-[11px] font-semibold text-[#F6F1E5]"
                              style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)" }}>Save</button>
                      <button onClick={() => setEditingId(null)} className="px-4 py-1.5 rounded-full text-[11px] font-medium border border-[#26201618] text-[#8A7E6B]">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#262016] whitespace-pre-line leading-relaxed">{idea.text}</p>
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2">
                        <button onClick={() => { setEditingId(idea.id); setEditText(idea.text); setEditCategory(idea.category); }}
                                data-testid={`idea-edit-${idea.id}`} title="Edit"
                                className="w-7 h-7 rounded-full flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:bg-[#26201608]">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => deleteIdea(idea.id)} data-testid={`idea-delete-${idea.id}`} title="Delete"
                                className="w-7 h-7 rounded-full flex items-center justify-center text-[#8A7E6B] hover:text-red-600 hover:bg-red-50">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5 mt-2.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                            style={{ background: cat.color + "15", color: cat.color, border: `1px solid ${cat.color}30` }}>
                        <cat.icon size={10} /> {cat.label}
                      </span>
                      {(idea.tags || []).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#26201608] text-[#8A7E6B] border border-[#26201610]">{t}</span>
                      ))}
                      <span className="text-[10px] text-[#8A7E6B] flex items-center gap-1 ml-auto">
                        <Clock size={9} /> {timeAgo(idea.created_at)}
                        {idea.updated_at !== idea.created_at && " · edited"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > ideas.length && (
        <p className="text-center text-xs text-[#8A7E6B]">Showing {ideas.length} of {total} entries</p>
      )}
    </div>
  );
}
